import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  CircularProgress,
  Container,
  Typography,
  Paper,
  Box,
  Alert,
  Card,
  CardHeader,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Modal,
  IconButton,
} from "@mui/material";
import { Settings, Visibility, CheckCircle, Warning, Close } from "@mui/icons-material";
import axios from "axios";
import { API_URI } from "../../constants/GlobalConstants";

const ProductionSettings = () => {
  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;
  const jwttoken = userInfo?.token;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [productionSettings, setProductionSettings] = useState({
    defaultOrientation: "vertical",
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewOrientation, setPreviewOrientation] = useState("vertical");
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${API_URI}/api/app/production-settings`,
          {
            headers: { Authorization: `Bearer ${jwttoken}` },
          }
        );

        if (data) {
          setProductionSettings({
            defaultOrientation: data.defaultOrientation || "vertical",
          });
        }
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setError("Failed to fetch production settings");
      }
    };

    if (jwttoken) {
      fetchSettings();
    }
  }, [jwttoken]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleOrientationChange = (e) => {
    setProductionSettings({
      ...productionSettings,
      defaultOrientation: e.target.value,
    });
  };

  const handlers = {
    handleSubmit: async (e) => {
      e.preventDefault();
      setLoading(true);
      setError("");
      setSuccess(false);

      try {
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwttoken}`,
          },
        };

        await axios.put(
          `${API_URI}/api/app/production-settings`,
          { defaultOrientation: productionSettings.defaultOrientation },
          config
        );

        setSuccess(true);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setError("Failed to save production settings");
      }
    },
    handlePreview: async (orientation) => {
      try {
        setLoading(true);
        // Clear previous preview URL
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }

        const response = await axios.get(
          `${API_URI}/api/app/preview-production-sheet`,
          {
            params: { orientation },
            headers: { Authorization: `Bearer ${jwttoken}` },
            responseType: "blob",
          }
        );

        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        const url = URL.createObjectURL(pdfBlob);
        setPreviewUrl(url);
        setPreviewOrientation(orientation);
        setPreviewOpen(true);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setError("Failed to load preview");
      }
    },
    handleClosePreview: () => {
      setPreviewOpen(false);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    },
  };

  const renderSectionCard = (title, icon, children, description) => (
    <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2, bgcolor: "background.paper" }}>
      <CardHeader
        avatar={icon}
        title={
          <Typography variant="h6" fontWeight="bold" color="primary">
            {title}
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        }
        sx={{ pb: 1 }}
      />
      <Box sx={{ p: 3 }}>{children}</Box>
    </Card>
  );

  if (loading && !productionSettings.defaultOrientation) {
    return (
      <Container maxWidth="md" sx={{ py: 4, bgcolor: "background.default" }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, bgcolor: "background.paper" }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="300px"
            flexDirection="column"
          >
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading production settings...
            </Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4, bgcolor: "background.default" }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, bgcolor: "background.paper" }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Settings fontSize="large" color="primary" />
            <Typography variant="h4" fontWeight="bold" color="primary">
              Production Settings
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Configure the default orientation for production sheet PDFs and preview the layout.
          </Typography>
        </Box>

        {/* Status Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} icon={<Warning />}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircle />}>
            Production settings updated successfully!
          </Alert>
        )}

        <form onSubmit={handlers.handleSubmit}>
          {/* Production Settings Section */}
          {renderSectionCard(
            "PDF Orientation",
            <Settings color="primary" />,
            <Box>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  name="defaultOrientation"
                  value={productionSettings.defaultOrientation}
                  onChange={handleOrientationChange}
                >
                  <FormControlLabel
                    value="vertical"
                    control={<Radio />}
                    label="Vertical"
                  />
                  <FormControlLabel
                    value="horizontal"
                    control={<Radio />}
                    label="Horizontal"
                  />
                </RadioGroup>
              </FormControl>
              <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Visibility />}
                  onClick={() => handlers.handlePreview("vertical")}
                  disabled={loading}
                >
                  Preview Vertical
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Visibility />}
                  onClick={() => handlers.handlePreview("horizontal")}
                  disabled={loading}
                >
                  Preview Horizontal
                </Button>
              </Box>
            </Box>,
            "Set the default PDF orientation for production sheets and preview the output"
          )}

          {/* Submit Button */}
          <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Settings />}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1.1rem",
              }}
            >
              {loading ? "Saving..." : "Save Settings"}
            </Button>
          </Box>
        </form>

        {/* Preview Modal */}
        <Modal
          open={previewOpen}
          onClose={handlers.handleClosePreview}
          aria-labelledby="preview-modal-title"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: previewOrientation === "horizontal" ? "90vw" : "70vw",
              maxWidth: previewOrientation === "horizontal" ? 1200 : 900,
              maxHeight: "90vh",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              overflow: "auto",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Typography id="preview-modal-title" variant="h6" component="h2">
                {previewOrientation === "horizontal" ? "Horizontal" : "Vertical"} Preview
              </Typography>
              <IconButton onClick={handlers.handleClosePreview}>
                <Close />
              </IconButton>
            </Box>
            <Box
              sx={{
                width: "100%",
                height: "70vh",
                border: "1px solid #ddd",
                bgcolor: "#fff",
                overflow: "hidden",
              }}
            >
              {previewUrl ? (
                <object
                  data={previewUrl}
                  type="application/pdf"
                  width="100%"
                  height="100%"
                >
                  <iframe
                    key={previewUrl}
                    src={previewUrl}
                    style={{ width: "100%", height: "100%", border: "none" }}
                    title="PDF Preview"
                  />
                </object>
              ) : (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                >
                  <CircularProgress />
                </Box>
              )}
            </Box>
          </Box>
        </Modal>
      </Paper>
    </Container>
  );
};

export default ProductionSettings;